import { useState } from "react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shared "type an email, press Enter/comma, get a removable chip" input —
// used by both the ad-hoc recipients field on a schedule and the
// recipient-list editor, so the validation/dedupe/max-count behavior only
// lives in one place.
export default function EmailChipInput({
  id,
  emails,
  onChange,
  disabled = false,
  max,
  placeholder = "name@company.com",
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState(null);

  const add = () => {
    const email = value.trim().toLowerCase();
    if (!email) return;

    if (!EMAIL_RE.test(email)) {
      setError(`"${email}" isn't a valid email address.`);
      return;
    }

    if (emails.includes(email)) {
      setError("That email is already added.");
      setValue("");
      return;
    }

    if (max && emails.length >= max) {
      setError(`You can add up to ${max} recipients.`);
      return;
    }

    onChange([...emails, email]);
    setValue("");
    setError(null);
  };

  const remove = (email) => onChange(emails.filter((e) => e !== email));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add();
    } else if (e.key === "Backspace" && !value && emails.length) {
      remove(emails[emails.length - 1]);
    }
  };

  const inputClass =
    "w-full bg-gray-950 border border-gray-700 focus:border-blue-500 rounded-lg px-3 py-2 text-sm text-white outline-none disabled:opacity-60";

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {emails.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-full pl-3 pr-1.5 py-1 text-xs text-gray-200"
          >
            {email}
            <button
              type="button"
              onClick={() => remove(email)}
              aria-label={`Remove ${email}`}
              disabled={disabled}
              className="w-4 h-4 rounded-full hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white"
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          id={id}
          type="email"
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(null); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || (max && emails.length >= max)}
          className={inputClass}
        />
        <button
          type="button"
          onClick={add}
          disabled={disabled || (max && emails.length >= max)}
          className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-sm font-medium disabled:opacity-50 shrink-0"
        >
          Add
        </button>
      </div>

      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
    </div>
  );
}
